import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Users,
  ChevronLeft,
  Download,
  Plus,
  UserCheck,
  TrendingUp,
  Wallet,
  X,
  Mail,
  Percent,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatNIS(agorot: number): string {
  return `\u20AA${(agorot / 100).toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join("");
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PartnerRow {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly commission_rate: number;
  readonly created_at: string;
  readonly referred_count: number;
  readonly total_commissions: number;
  readonly pending_commissions: number;
}

interface CommissionRow {
  readonly id: string;
  readonly tenant_name: string;
  readonly amount: number;
  readonly period: string;
  readonly status: "pending" | "paid";
  readonly paid_at: string | null;
}

interface ReferralRow {
  readonly id: string;
  readonly tenant_name: string;
  readonly tenant_status: string;
  readonly subscription_amount: number;
  readonly referred_at: string;
}

// ---------------------------------------------------------------------------
// Status config — derived from data, no server-side status field
// ---------------------------------------------------------------------------

type DerivedStatus = "active" | "pending" | "inactive";

function deriveStatus(partner: PartnerRow): DerivedStatus {
  if (partner.referred_count > 0 && partner.total_commissions > 0) return "active";
  if (partner.referred_count > 0) return "pending";
  return "inactive";
}

interface PartnerStatusConfig {
  readonly label: string;
  readonly badge: "success" | "warning" | "secondary";
  readonly dotColor: string;
  readonly avatarRing: string;
}

const PARTNER_STATUS_CONFIG: Record<DerivedStatus, PartnerStatusConfig> = {
  active: {
    label: "פעיל",
    badge: "success",
    dotColor: "bg-green-500",
    avatarRing: "ring-2 ring-green-500/40",
  },
  pending: {
    label: "ממתין",
    badge: "warning",
    dotColor: "bg-yellow-500",
    avatarRing: "ring-2 ring-yellow-500/40",
  },
  inactive: {
    label: "לא פעיל",
    badge: "secondary",
    dotColor: "bg-gray-400",
    avatarRing: "ring-2 ring-gray-300/40",
  },
};

// Color palette for avatars — cycles by index
const AVATAR_COLORS = [
  "from-violet-500 to-purple-600",
  "from-blue-500 to-indigo-600",
  "from-emerald-500 to-teal-600",
  "from-amber-500 to-orange-600",
] as const;

// ---------------------------------------------------------------------------
// Add Partner Modal
// ---------------------------------------------------------------------------

interface AddPartnerModalProps {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function AddPartnerModal({ onClose, onCreated }: AddPartnerModalProps): ReactNode {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [commissionRate, setCommissionRate] = useState("10");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(() => {
    const rate = parseInt(commissionRate, 10);
    if (!name.trim() || !email.trim() || isNaN(rate) || rate < 1 || rate > 50) {
      setError("נא למלא את כל השדות בצורה תקינה");
      return;
    }

    setSubmitting(true);
    setError(null);

    fetch("/super/api/partners", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: name.trim(), email: email.trim(), commission_rate: rate }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; error?: string }>;
      })
      .then((json) => {
        if (!json.success) throw new Error(json.error ?? "API error");
        onCreated();
        onClose();
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "שגיאה ביצירת שותף");
      })
      .finally(() => setSubmitting(false));
  }, [name, email, commissionRate, onCreated, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      dir="rtl"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Sheet */}
      <div className="relative z-10 w-full sm:max-w-md bg-background rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 space-y-5 animate-in slide-in-from-bottom-4 duration-300">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">הוסף שותף חדש</h2>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-muted transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              שם מלא
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ישראל ישראלי"
              className="min-h-[44px]"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              אימייל
            </label>
            <div className="relative">
              <Mail className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="partner@example.com"
                dir="ltr"
                className="min-h-[44px] pe-9"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              אחוז עמלה
            </label>
            <div className="relative">
              <Percent className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="number"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                placeholder="10"
                min={1}
                max={50}
                className="min-h-[44px] pe-9"
              />
            </div>
          </div>
          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          <Button
            className="flex-1 min-h-[44px]"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? "שומר..." : "הוסף שותף"}
          </Button>
          <Button
            variant="outline"
            className="min-h-[44px] px-5"
            onClick={onClose}
            disabled={submitting}
          >
            ביטול
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Partner card
// ---------------------------------------------------------------------------

interface PartnerCardProps {
  readonly partner: PartnerRow;
  readonly colorClass: string;
  readonly maxReferrals: number;
  readonly onSelect: (p: PartnerRow) => void;
}

function PartnerCard({
  partner,
  colorClass,
  maxReferrals,
  onSelect,
}: PartnerCardProps): ReactNode {
  const status = deriveStatus(partner);
  const sc = PARTNER_STATUS_CONFIG[status];
  const barWidth = maxReferrals > 0
    ? Math.round((partner.referred_count / maxReferrals) * 100)
    : 0;

  return (
    <Card className="group border shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 overflow-hidden">
      {/* Top accent bar */}
      <div className={`h-1 w-full bg-gradient-to-r ${colorClass}`} />
      <CardContent className="p-5 space-y-4">
        {/* Header row */}
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <Avatar
              className={`h-12 w-12 ${sc.avatarRing} transition-all duration-200`}
            >
              <AvatarFallback
                className={`bg-gradient-to-br ${colorClass} text-white font-bold text-sm`}
              >
                {getInitials(partner.name)}
              </AvatarFallback>
            </Avatar>
            {/* Status dot */}
            <span
              className={`absolute bottom-0 left-0 h-3 w-3 rounded-full border-2 border-background ${sc.dotColor}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-sm truncate">{partner.name}</p>
              <Badge variant={sc.badge} className="text-[10px] px-1.5">
                {sc.label}
              </Badge>
            </div>
            <p
              className="text-xs text-muted-foreground truncate mt-0.5"
              dir="ltr"
            >
              {partner.email}
            </p>
          </div>
          {/* Commission rate badge */}
          <div className="shrink-0 text-center bg-primary/10 rounded-xl px-2.5 py-1.5">
            <p className="text-xl font-extrabold text-primary leading-none tabular-nums">
              {partner.commission_rate}%
            </p>
            <p className="text-[9px] text-primary/70 font-medium mt-0.5">עמלה</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted/40 rounded-lg p-2.5 space-y-0.5">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              סה&quot;כ הרוויח
            </p>
            <p className="text-sm font-bold tabular-nums">
              {formatNIS(partner.total_commissions)}
            </p>
          </div>
          <div className="bg-muted/40 rounded-lg p-2.5 space-y-0.5">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              ממתין לתשלום
            </p>
            <p
              className={`text-sm font-bold tabular-nums ${
                partner.pending_commissions > 0
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-muted-foreground"
              }`}
            >
              {partner.pending_commissions > 0
                ? formatNIS(partner.pending_commissions)
                : "—"}
            </p>
          </div>
        </div>

        {/* Referrals mini bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>הפניות</span>
            <span className="font-semibold tabular-nums">
              {partner.referred_count}
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted/50">
            <div
              className={`h-1.5 rounded-full bg-gradient-to-r ${colorClass} transition-all duration-700`}
              style={{ width: `${String(barWidth)}%` }}
            />
          </div>
        </div>

        {/* CTA */}
        <Button
          variant="outline"
          size="sm"
          className="w-full min-h-[44px] text-xs font-medium group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-200 cursor-pointer"
          onClick={() => onSelect(partner)}
        >
          צפה בפרטים
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Detail view
// ---------------------------------------------------------------------------

interface DetailViewProps {
  readonly partner: PartnerRow;
  readonly partnerIndex: number;
  readonly onBack: () => void;
}

function DetailView({ partner, partnerIndex, onBack }: DetailViewProps): ReactNode {
  const [commissions, setCommissions] = useState<ReadonlyArray<CommissionRow>>([]);
  const [referrals, setReferrals] = useState<ReadonlyArray<ReferralRow>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const status = deriveStatus(partner);
  const sc = PARTNER_STATUS_CONFIG[status];
  const colorClass = AVATAR_COLORS[partnerIndex % AVATAR_COLORS.length] ?? AVATAR_COLORS[0];

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`/super/api/partners/${partner.id}/commissions`, { credentials: "include" })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<{ success: boolean; data: CommissionRow[] }>;
        }),
      fetch(`/super/api/partners/${partner.id}/referrals`, { credentials: "include" })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<{ success: boolean; data: ReferralRow[] }>;
        }),
    ])
      .then(([commissionsRes, referralsRes]) => {
        if (!commissionsRes.success) throw new Error("Failed to load commissions");
        if (!referralsRes.success) throw new Error("Failed to load referrals");
        setCommissions(commissionsRes.data);
        setReferrals(referralsRes.data);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setLoading(false));
  }, [partner.id]);

  const exportCSV = useCallback(() => {
    if (commissions.length === 0) return;
    const BOM = "\uFEFF";
    const header = "תקופה,דייר,סכום,סטטוס,תאריך תשלום\n";
    const rows = commissions
      .map(
        (c) =>
          `${c.period},${c.tenant_name},${(c.amount / 100).toFixed(2)},${
            c.status === "paid" ? "שולם" : "ממתין"
          },${c.paid_at ?? ""}`,
      )
      .join("\n");
    const blob = new Blob([BOM + header + rows], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `commissions-${partner.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [commissions, partner.name]);

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="min-h-[44px] min-w-[44px] rounded-full"
          onClick={onBack}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <Avatar className={`h-10 w-10 ${sc.avatarRing}`}>
          <AvatarFallback
            className={`bg-gradient-to-br ${colorClass} text-white font-bold text-sm`}
          >
            {getInitials(partner.name)}
          </AvatarFallback>
        </Avatar>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">{partner.name}</h1>
            <Badge variant={sc.badge}>{sc.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground" dir="ltr">
            {partner.email}
          </p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        {[
          {
            label: "לקוחות מופנים",
            value: String(partner.referred_count),
            icon: <UserCheck className="h-4 w-4" />,
            color: "text-blue-600 dark:text-blue-400",
            bg: "bg-blue-500/10",
          },
          {
            label: "שיעור עמלה",
            value: `${String(partner.commission_rate)}%`,
            icon: <Percent className="h-4 w-4" />,
            color: "text-violet-600 dark:text-violet-400",
            bg: "bg-violet-500/10",
          },
          {
            label: "סה\"כ עמלות",
            value: formatNIS(partner.total_commissions),
            icon: <TrendingUp className="h-4 w-4" />,
            color: "text-emerald-600 dark:text-emerald-400",
            bg: "bg-emerald-500/10",
          },
          {
            label: "ממתין לתשלום",
            value: formatNIS(partner.pending_commissions),
            icon: <Wallet className="h-4 w-4" />,
            color:
              partner.pending_commissions > 0
                ? "text-amber-600 dark:text-amber-400"
                : "text-muted-foreground",
            bg:
              partner.pending_commissions > 0
                ? "bg-amber-500/10"
                : "bg-muted/30",
          },
        ].map((kpi) => (
          <Card key={kpi.label} className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`rounded-lg p-2 ${kpi.bg} ${kpi.color} shrink-0`}>
                {kpi.icon}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
                  {kpi.label}
                </p>
                <p className={`text-lg font-extrabold tabular-nums ${kpi.color}`}>
                  {kpi.value}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading && (
        <div className="py-8 text-center text-muted-foreground text-sm">טוען...</div>
      )}
      {!loading && error && (
        <div className="py-8 text-center text-destructive text-sm">{error}</div>
      )}

      {!loading && !error && (
        <>
          {/* Referrals */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                לקוחות מופנים
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {referrals.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  אין לקוחות
                </p>
              ) : (
                referrals.map((r, idx) => (
                  <div
                    key={r.id}
                    className={`flex flex-wrap items-center gap-3 rounded-xl px-4 py-3 ${
                      idx % 2 === 0 ? "bg-muted/20" : "bg-background"
                    }`}
                  >
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                      {r.tenant_name.charAt(0)}
                    </div>
                    <span className="font-medium text-sm flex-1">{r.tenant_name}</span>
                    <Badge
                      variant={r.tenant_status === "active" ? "success" : "danger"}
                    >
                      {r.tenant_status === "active" ? "פעיל" : "מושהה"}
                    </Badge>
                    <span className="text-sm tabular-nums font-semibold">
                      {formatNIS(r.subscription_amount)}
                    </span>
                    <span className="text-xs text-muted-foreground min-w-[70px] text-end">
                      {formatDate(r.referred_at)}
                    </span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Commissions */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                עמלות
              </CardTitle>
              {commissions.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-[36px] gap-1.5 text-xs"
                  onClick={exportCSV}
                >
                  <Download className="h-3.5 w-3.5" />
                  ייצוא CSV
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {commissions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  אין עמלות
                </p>
              ) : (
                commissions.map((c, idx) => (
                  <div
                    key={c.id}
                    className={`flex flex-wrap items-center gap-3 rounded-xl px-4 py-3 ${
                      idx % 2 === 0 ? "bg-muted/20" : "bg-background"
                    }`}
                  >
                    <span className="text-xs font-mono text-muted-foreground min-w-[56px]">
                      {c.period}
                    </span>
                    <span className="text-sm font-medium flex-1">{c.tenant_name}</span>
                    <span className="text-sm font-bold tabular-nums">
                      {formatNIS(c.amount)}
                    </span>
                    <Badge variant={c.status === "paid" ? "success" : "warning"}>
                      {c.status === "paid" ? "שולם" : "ממתין"}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PartnersPage(): ReactNode {
  const [partners, setPartners] = useState<ReadonlyArray<PartnerRow>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<{ partner: PartnerRow; index: number } | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchPartners = useCallback(() => {
    setLoading(true);
    fetch("/super/api/partners", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; data: PartnerRow[] }>;
      })
      .then((json) => {
        if (!json.success) throw new Error("API error");
        setPartners(json.data);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  // Detail view
  if (selectedPartner) {
    return (
      <DetailView
        partner={selectedPartner.partner}
        partnerIndex={selectedPartner.index}
        onBack={() => setSelectedPartner(null)}
      />
    );
  }

  // Aggregate stats
  const totalReferrals = partners.reduce((acc, p) => acc + p.referred_count, 0);
  const totalCommissions = partners.reduce((acc, p) => acc + p.total_commissions, 0);
  const activeCount = partners.filter((p) => deriveStatus(p) === "active").length;
  const maxReferrals = partners.length > 0
    ? Math.max(...partners.map((p) => p.referred_count))
    : 0;

  return (
    <>
      {showAddModal && (
        <AddPartnerModal
          onClose={() => setShowAddModal(false)}
          onCreated={fetchPartners}
        />
      )}

      <div className="p-4 md:p-6 space-y-8" dir="rtl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">שותפים</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              ניהול תוכנית השותפים והעמלות
            </p>
          </div>
          <Button
            className="min-h-[44px] gap-2"
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">הוסף שותף</span>
          </Button>
        </div>

        {/* Loading / error */}
        {loading && (
          <div className="py-12 text-center text-muted-foreground text-sm">טוען...</div>
        )}
        {!loading && error && (
          <div className="py-12 text-center text-destructive text-sm">{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* Stats row */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
              {[
                {
                  title: "שותפים פעילים",
                  value: String(activeCount),
                  sub: `מתוך ${String(partners.length)} שותפים`,
                  icon: <Users className="h-5 w-5" />,
                  gradient:
                    "from-blue-50 to-indigo-50/60 dark:from-blue-950/30 dark:to-indigo-900/20",
                  iconBg: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
                  titleColor: "text-blue-700 dark:text-blue-400",
                  valueColor: "text-blue-900 dark:text-blue-100",
                },
                {
                  title: "סה\"כ הפניות",
                  value: String(totalReferrals),
                  sub: "לקוחות שהופנו",
                  icon: <UserCheck className="h-5 w-5" />,
                  gradient:
                    "from-violet-50 to-purple-50/60 dark:from-violet-950/30 dark:to-purple-900/20",
                  iconBg: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
                  titleColor: "text-violet-700 dark:text-violet-400",
                  valueColor: "text-violet-900 dark:text-violet-100",
                },
                {
                  title: "עמלות ששולמו",
                  value: formatNIS(totalCommissions),
                  sub: "מאז ההקמה",
                  icon: <TrendingUp className="h-5 w-5" />,
                  gradient:
                    "from-emerald-50 to-teal-50/60 dark:from-emerald-950/30 dark:to-teal-900/20",
                  iconBg: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
                  titleColor: "text-emerald-700 dark:text-emerald-400",
                  valueColor: "text-emerald-900 dark:text-emerald-100",
                },
              ].map((kpi) => (
                <Card
                  key={kpi.title}
                  className={`border-0 shadow-md bg-gradient-to-br ${kpi.gradient} backdrop-blur-sm transition-transform hover:scale-[1.01] duration-200`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1.5">
                        <p
                          className={`text-xs font-semibold uppercase tracking-widest ${kpi.titleColor}`}
                        >
                          {kpi.title}
                        </p>
                        <p
                          className={`text-3xl font-extrabold tabular-nums leading-none ${kpi.valueColor}`}
                        >
                          {kpi.value}
                        </p>
                        <p className={`text-xs ${kpi.titleColor} opacity-70`}>
                          {kpi.sub}
                        </p>
                      </div>
                      <div
                        className={`rounded-xl p-2.5 ${kpi.iconBg} bg-white/20 dark:bg-black/20`}
                      >
                        {kpi.icon}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Partner cards grid */}
            {partners.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center space-y-3">
                  <Users className="mx-auto h-10 w-10 text-muted-foreground" />
                  <p className="text-muted-foreground">אין שותפים עדיין</p>
                  <Button onClick={() => setShowAddModal(true)} className="min-h-[44px]">
                    <Plus className="h-4 w-4 mr-2" />
                    הוסף שותף ראשון
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                {partners.map((partner, idx) => (
                  <PartnerCard
                    key={partner.id}
                    partner={partner}
                    colorClass={AVATAR_COLORS[idx % AVATAR_COLORS.length] ?? AVATAR_COLORS[0]}
                    maxReferrals={maxReferrals}
                    onSelect={(p) => setSelectedPartner({ partner: p, index: idx })}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
